/* e3-fly-to-cart.js — page-cart-mode feedback engine.
   On add-to-cart, flies a clone of the pressed button into the visible cart icon,
   refreshes the count from the server, and pulses the icon — so the shopper stays
   on the page instead of redirecting to /cart. Classic deferred script: depends on
   the bare globals `subscribe` / `PUB_SUB_EVENTS` from global.js (loaded earlier in
   document order). Redirect suppression lives in product-form.js / product-bundle.js. */
(function () {
  'use strict';

  if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
  // Drawer mode supplies its own feedback — no-op so the engine is safe if cart type
  // is ever switched back to drawer.
  if (document.querySelector('cart-drawer')) return;

  var ADD_SELECTOR = '.add-to-cart-button, [data-bundle-addtocart]';
  var SOURCE_TTL = 3000;
  var pending = null; // fallback path (surfaces without a .loading state, e.g. bundle)
  var charge = null;  // the clone currently pulsing over a button during its .loading phase

  function reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  // Capture phase: the bundle/quick-add handlers stop propagation on some surfaces,
  // so listen on the way down. Clone at press time to capture the resting label
  // (the live button swaps to a spinner during the AJAX add).
  function onPress(e) {
    var btn = e.target.closest && e.target.closest(ADD_SELECTOR);
    if (!btn) return;
    var rect = btn.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    pending = { node: btn.cloneNode(true), rect: rect, time: Date.now() };
    // For .add-to-cart-button surfaces, charge a clone in place the moment the button
    // enters its .loading state (fills the spinner gap), then fly it on cartUpdate.
    if (!reducedMotion() && btn.classList.contains('add-to-cart-button')) watchLoading(btn);
  }
  document.addEventListener('pointerdown', onPress, true);
  document.addEventListener('click', onPress, true);

  // One-shot: when the pressed button enters .loading (the add genuinely started),
  // begin the charge. Give up after 1.5s if loading never starts (validation blocked
  // the submit). __e3Watching guards the double pointerdown+click press.
  function watchLoading(btn) {
    if (btn.__e3Watching) return;
    btn.__e3Watching = true;
    var stop = function () { obs.disconnect(); btn.__e3Watching = false; };
    var obs = new MutationObserver(function () {
      if (btn.classList.contains('loading')) { stop(); startCharge(btn); }
    });
    obs.observe(btn, { attributes: true, attributeFilter: ['class'] });
    setTimeout(stop, 1500);
  }

  // Build the charge clone over the button and pulse it in place. Clone fresh in its
  // resting look (strip .loading + hide the spinner) so it shows the label, not a
  // frozen spinner.
  function startCharge(btn) {
    if (charge) removeCharge();
    var rect = btn.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    var node = btn.cloneNode(true);
    node.classList.remove('loading');
    var sp = node.querySelector('.loading__spinner');
    if (sp) sp.classList.add('hidden');
    var wrap = buildCloneWrap(node, rect);
    // Inflate symmetrically so the pulse's scale(1) floor still fully covers the
    // button + its border (no dark edge peeking). Center is preserved, so the flight
    // launch (computed from charge.rect) is unaffected.
    var PAD = 4;
    wrap.style.left = (rect.left - PAD) + 'px';
    wrap.style.top = (rect.top - PAD) + 'px';
    wrap.style.width = (rect.width + PAD * 2) + 'px';
    wrap.style.height = (rect.height + PAD * 2) + 'px';
    wrap.classList.add('e3-charging');
    document.body.appendChild(wrap);
    charge = { wrapper: wrap, rect: rect, btn: btn, flying: false, timeout: null };
    // Hide the real button so it can't show through the clone (secondary buttons have a
    // translucent fill). visibility (not display) keeps its layout box — no reflow.
    // Restored at launch (clone peels off, button stays) and in removeCharge (failure).
    btn.style.visibility = 'hidden';
    watchChargeEnd(btn);
  }

  // If .loading is removed while we haven't handed off to flight, the add failed or
  // was blocked — clean up the charge. Safety timeout clears a stuck charge.
  function watchChargeEnd(btn) {
    var obs = new MutationObserver(function () {
      if (!btn.classList.contains('loading')) {
        obs.disconnect();
        if (charge && !charge.flying) removeCharge();
      }
    });
    obs.observe(btn, { attributes: true, attributeFilter: ['class'] });
    charge.timeout = setTimeout(function () {
      obs.disconnect();
      if (charge && !charge.flying) removeCharge();
    }, 6000);
  }

  function removeCharge() {
    if (!charge) return;
    var w = charge.wrapper;
    if (charge.timeout) clearTimeout(charge.timeout);
    if (charge.btn) charge.btn.style.visibility = ''; // restore the hidden button
    charge = null;
    if (!w) return;
    w.classList.remove('e3-charging');
    var a = w.animate([{ opacity: 1 }, { opacity: 0, transform: 'scale(0.9)' }],
      { duration: 180, easing: 'ease-out', fill: 'forwards' });
    a.onfinish = function () { w.remove(); };
    a.oncancel = function () { w.remove(); };
  }

  function isOnScreen(el) {
    if (!el) return false;
    var cs = getComputedStyle(el);
    if (cs.visibility === 'hidden' || parseFloat(cs.opacity) === 0) return false;
    var r = el.getBoundingClientRect();
    if (r.width === 0 || r.height === 0) return false; // 0×0 also catches display:none ancestors
    var cx = r.left + r.width / 2, cy = r.top + r.height / 2;
    return cx >= 0 && cx <= window.innerWidth && cy >= 0 && cy <= window.innerHeight;
  }

  function pickTarget() {
    var header = document.querySelector('#cart-icon-bubble .svg-wrapper');
    var toolbar = document.querySelector('toolbar-mobile a[href$="/cart"] .svg-wrapper, toolbar-mobile .count-bubble .svg-wrapper');
    var list = [header, toolbar].filter(Boolean);
    for (var i = 0; i < list.length; i++) if (isOnScreen(list[i])) return list[i];
    return header || toolbar || null;
  }

  function pulse(target) {
    var el = target || pickTarget();
    if (!el) return;
    el.classList.remove('e3-cart-pulse');
    void el.offsetWidth; // restart the keyframes
    el.classList.add('e3-cart-pulse');
    // Timeout, not animationend: the bump/ring (and the nested count-pop) all bubble
    // their animationend to this wrapper, so a listener would fire on the shortest.
    // 560ms covers the longest child animation (the 520ms ring).
    setTimeout(function () { el.classList.remove('e3-cart-pulse'); }, 560);
  }

  // The header bubble is only server-rendered when the cart is non-empty
  // (header-functions.liquid) — create it on the first add from an empty cart so the
  // count has somewhere to land. The mobile toolbar always renders its own bubble.
  function ensureHeaderBubble(count) {
    var icon = document.getElementById('cart-icon-bubble');
    if (!icon) return;
    var wrap = icon.querySelector('.svg-wrapper');
    if (!wrap || wrap.querySelector('.cart-count-bubble') || count <= 0) return;
    var bubble = document.createElement('span');
    bubble.className = 'cart-count-bubble';
    var num = document.createElement('span');
    num.setAttribute('aria-hidden', 'true');
    num.setAttribute('data-cart-count', '');
    var sr = document.createElement('span');
    sr.className = 'visually-hidden';
    sr.setAttribute('data-cart-text', '');
    bubble.appendChild(num);
    bubble.appendChild(sr);
    wrap.appendChild(bubble);
  }

  // Server total (not +1) so quantity>1, multi-item bundles, and cart merges are
  // correct. Write the bare integer — the "Cart (N)" parens/font are presentational
  // in e3-header.css.
  function refreshCount() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var count = cart.item_count;
        var text = count < 100 ? String(count) : '99+';
        ensureHeaderBubble(count);
        document.querySelectorAll('[data-cart-count]').forEach(function (el) {
          el.textContent = text;
          popCount(el);
        });
      })
      .catch(function () {});
  }

  // Pop the count digit when it changes. Targets the number span itself (not the
  // .cart-count-bubble, whose position transform in e3-header.css must not be
  // clobbered). Reduced-motion neutralizes the keyframes in CSS.
  function popCount(el) {
    el.classList.remove('e3-count-pop');
    void el.offsetWidth; // restart
    el.classList.add('e3-count-pop');
    el.addEventListener('animationend', function () {
      el.classList.remove('e3-count-pop');
    }, { once: true });
  }

  // Position a fixed clone wrapper over a button rect (not yet appended).
  function buildCloneWrap(node, rect) {
    var wrap = document.createElement('div');
    wrap.className = 'e3-fly-clone';
    wrap.setAttribute('aria-hidden', 'true');
    wrap.style.left = rect.left + 'px';
    wrap.style.top = rect.top + 'px';
    wrap.style.width = rect.width + 'px';
    wrap.style.height = rect.height + 'px';
    node.removeAttribute('id'); // avoid duplicate ProductSubmitButton-* ids
    node.style.margin = '0';
    node.style.width = '100%';
    node.style.height = '100%';
    wrap.appendChild(node);
    return wrap;
  }

  // Fly an already-appended wrapper from its origin rect into the visible cart icon.
  // Stays opaque + readable for most of the flight (snap out only in the last 20%),
  // arcs high, lands at a recognizable size — then the icon reacts (pulse on finish).
  function runFlight(wrap, rect) {
    var target = pickTarget();
    if (!target) { wrap.remove(); charge = null; pulse(null); return; }
    var t = target.getBoundingClientRect();
    var dx = (t.left + t.width / 2) - (rect.left + rect.width / 2);
    var dy = (t.top + t.height / 2) - (rect.top + rect.height / 2);

    var anim = wrap.animate([
      { transform: 'translate(0px, 0px) scale(1)', opacity: 1, offset: 0 },
      { transform: 'translate(' + dx * 0.5 + 'px, ' + (dy * 0.5 - 110) + 'px) scale(0.62)', opacity: 1, offset: 0.5 },
      { transform: 'translate(' + dx * 0.85 + 'px, ' + dy * 0.85 + 'px) scale(0.4)', opacity: 1, offset: 0.8 },
      { transform: 'translate(' + dx + 'px, ' + dy + 'px) scale(0.28)', opacity: 0, offset: 1 }
    ], { duration: 720, easing: 'cubic-bezier(0.5, 0, 0.4, 1)', fill: 'forwards' });

    anim.onfinish = function () { wrap.remove(); charge = null; pulse(target); };
    anim.oncancel = function () { wrap.remove(); charge = null; };
  }

  // Hand the charging clone off to flight (same element — seamless).
  function launch() {
    charge.wrapper.classList.remove('e3-charging');
    if (charge.timeout) clearTimeout(charge.timeout);
    if (charge.btn) charge.btn.style.visibility = ''; // clone peels off; real button stays
    runFlight(charge.wrapper, charge.rect);
  }

  // Fallback (no charge clone, e.g. the bundle button): build + append + fly.
  function fly(node, rect) {
    var wrap = buildCloneWrap(node, rect);
    document.body.appendChild(wrap);
    runFlight(wrap, rect);
  }

  subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
    refreshCount();
    // Preferred path: a clone is already charging over the button — fly that one.
    if (charge && !charge.flying) {
      charge.flying = true;
      pending = null;
      launch();
      return;
    }
    if (!pending || (Date.now() - pending.time) >= SOURCE_TTL) return;
    var p = pending;
    pending = null; // consume once
    if (reducedMotion()) { pulse(pickTarget()); return; }
    fly(p.node, p.rect);
  });
})();
